
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Category, ListingTier, Professional, GalleryImage } from '../types';
import { CATEGORIES, STATES } from '../constants';
import { Save, Building2, MapPin, CheckCircle, ArrowLeft, AlertTriangle, Loader2, Lock, Phone, Mail, FileText, Globe, Upload, Image as ImageIcon, Plus, Trash2, Sparkles, Wand2 } from 'lucide-react';
import { listingService } from '../services/listingService';
import { authService } from '../services/authService';
import { autofillListingFromUrl } from '../services/geminiService';

const ListingFormPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tier = (queryParams.get('tier') as ListingTier) || ListingTier.FREE;
  const editId = queryParams.get('edit');
  const user = useMemo(() => authService.getUser(), []);

  const [formData, setFormData] = useState<Partial<Professional>>({
    businessName: '',
    category: Category.TOWN_PLANNING,
    tier: tier,
    rating: 5.0,
    reviewCount: 0,
    abn: '',
    location: { state: 'VIC', suburb: '', address: '', lat: -37.8136, lng: 144.9631 },
    contact: { phone: '', email: user?.email || '', website: '' }, 
    description: '',
    tags: [],
    gallery: []
  });

  const [autofillUrl, setAutofillUrl] = useState('');
  const [isAutofilling, setIsAutofilling] = useState(false);
  const [descriptionBuffer, setDescriptionBuffer] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ success: boolean; mode: 'cloud' | 'local' } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login?redirect=' + encodeURIComponent(location.pathname + location.search));
      return;
    }

    const load = async () => {
      if (editId) {
        try {
          const found = await listingService.getListingById(editId);
          if (found) {
            if (found.contact.email !== user?.email && user?.email !== 'planspace@outlook.com') {
              setError("Security Error: You do not have permission to edit this listing.");
              setIsLoading(false);
              return;
            }
            setFormData(found);
            setDescriptionBuffer(found.description || '');
          } else {
            setError("Listing not found.");
          }
        } catch (err) {
          setError("Failed to load listing details.");
        }
      }
      setIsLoading(false);
    };
    load();
  }, [editId, user?.email, navigate]);

  const handleAutofill = async () => {
    const input = autofillUrl.trim();
    if (!input || !input.includes('.')) {
      setError("Please enter a valid website URL for AI Magic Fill.");
      return;
    }
    
    setIsAutofilling(true);
    setError(null);
    try {
      const data = await autofillListingFromUrl(input);
      
      setFormData(prev => ({
        ...prev,
        businessName: data.businessName || prev.businessName,
        category: (data.category as Category) || prev.category,
        abn: data.abn || prev.abn,
        location: {
          ...prev.location!,
          suburb: data.location?.suburb || prev.location?.suburb || '',
          state: (data.location?.state && STATES.includes(data.location.state)) ? data.location.state : prev.location?.state || 'VIC',
          address: data.location?.address || prev.location?.address || ''
        },
        contact: {
          ...prev.contact!,
          phone: data.contact?.phone || prev.contact?.phone || '',
          email: data.contact?.email || prev.contact?.email || user?.email || '',
          website: input.startsWith('http') ? input : `https://${input}`
        },
        logoUrl: data.logoUrl || prev.logoUrl,
        imageUrl: data.imageUrl || prev.imageUrl
      }));
      
      if (data.description) {
        setDescriptionBuffer(data.description);
      }
      
      if (data.businessName) setAutofillUrl('');
    } catch (err: any) {
      setError(err.message || "PlanStack AI was unable to extract data from this website.");
    } finally {
      setIsAutofilling(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'imageUrl' | 'logoUrl') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, [field]: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addGalleryItem = () => {
    setFormData(prev => ({
      ...prev,
      gallery: [...(prev.gallery || []), { url: '', description: '' }]
    }));
  };

  const updateGalleryItem = (index: number, field: keyof GalleryImage, value: string) => {
    setFormData(prev => {
      const newGallery = [...(prev.gallery || [])];
      newGallery[index] = { ...newGallery[index], [field]: value };
      return { ...prev, gallery: newGallery };
    });
  };

  const handleGalleryFileUpload = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateGalleryItem(index, 'url', reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeGalleryItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      gallery: (prev.gallery || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (error || saving) return;

    setSaving(true);
    setError(null);
    
    const finalWebsite = formData.contact?.website?.trim();
    const formattedWebsite = finalWebsite && !/^https?:\/\//i.test(finalWebsite) ? `https://${finalWebsite}` : finalWebsite;

    const dataToSave = {
      ...formData,
      description: descriptionBuffer,
      id: editId || `pro-${Date.now()}`,
      isVerified: editId ? formData.isVerified : false, 
      createdAt: formData.createdAt || new Date().toISOString(),
      rating: formData.rating || 5.0,
      reviewCount: formData.reviewCount || 0,
      contact: {
        ...formData.contact,
        email: formData.contact?.email || user?.email || '',
        website: formattedWebsite
      }
    } as Professional;
    
    try {
      const result = await listingService.saveListing(dataToSave);
      if (result.success) {
        setSaveResult({ success: true, mode: result.mode });
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setError(result.error || 'The system was unable to save the listing.');
      }
    } catch (err: any) {
      setError(err.message || 'A critical error occurred while saving.');
    } finally {
      setSaving(false);
    }
  };

  const updateNestedContact = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, contact: { ...prev.contact!, [field]: value } }));
  };

  const updateNestedLocation = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, location: { ...prev.location!, [field]: value } }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-brand-600 mb-4" size={32} />
        <div className="text-gray-400 font-black uppercase tracking-widest text-xs">Accessing Secure Records...</div>
      </div>
    );
  }

  if (saveResult?.success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center p-12 bg-white rounded-3xl shadow-xl border border-gray-100 max-w-md w-full animate-fade-in">
           <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
           </div>
           <h2 className="text-2xl font-black text-gray-900 mb-2">{editId ? 'Changes Saved!' : 'Listing Published!'}</h2>
           <p className="text-gray-500 mb-6">Synchronizing with national directory...</p>
           <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest bg-green-50 text-green-700">
              <CheckCircle size={14} /> Global Cloud Relay Active
           </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-12">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-xs font-black text-gray-400 hover:text-brand-600 transition uppercase tracking-widest">
            <ArrowLeft size={16} /> Back to Dashboard
          </button>
          <div className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">
            <Lock size={12} className="text-green-500" /> Secure Entry
          </div>
        </div>

        <div className="mb-10 text-center">
           <h1 className="text-3xl font-black text-gray-900 uppercase tracking-tight">
             {editId ? 'Update Practice Listing' : 'Professional Onboarding'}
           </h1>
           <p className="text-gray-500 mt-2 font-medium">Directory Tier: <span className="text-brand-600 font-black">{formData.tier} Account</span></p>
        </div>

        {/* AI MAGIC AUTOFILL BANNER */}
        {!editId && (
          <div className="mb-8 bg-gradient-to-r from-brand-900 to-blue-900 rounded-[2rem] p-1 shadow-2xl overflow-hidden animate-fade-in">
            <div className="bg-brand-900/90 backdrop-blur-md rounded-[1.8rem] p-8 md:p-10 flex flex-col md:flex-row items-center gap-8 border border-white/5">
              <div className="flex-shrink-0 bg-blue-500/20 p-4 rounded-3xl text-blue-400">
                <Sparkles size={32} className="animate-pulse" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-black text-white uppercase tracking-tight mb-2 flex items-center justify-center md:justify-start gap-2">
                  AI Magic Autofill <span className="bg-blue-600 text-[9px] px-2 py-0.5 rounded-md font-black">Beta</span>
                </h3>
                <p className="text-brand-200 text-sm font-medium mb-6">
                  Paste your business website URL and let PlanStack AI prepopulate your listing details in seconds.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <input 
                      type="text" 
                      placeholder="e.g. www.yourfirm.com.au" 
                      className="w-full bg-white/5 border border-white/10 rounded-xl py-3.5 pl-12 pr-4 text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-blue-500/50 transition"
                      value={autofillUrl}
                      onChange={(e) => setAutofillUrl(e.target.value)}
                    />
                  </div>
                  <button 
                    type="button"
                    onClick={handleAutofill}
                    disabled={isAutofilling || !autofillUrl}
                    className="bg-blue-600 hover:bg-blue-500 text-white font-black px-8 py-3.5 rounded-xl uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 transition shadow-lg active:scale-95 disabled:opacity-50"
                  >
                    {isAutofilling ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />}
                    {isAutofilling ? 'AI Researching...' : 'Fill with AI'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="animate-fade-in max-w-2xl mx-auto mb-8">
             <div className="bg-red-50 border-2 border-red-100 rounded-3xl p-6 text-center">
                <div className="flex items-center justify-center gap-3 text-red-600">
                   <AlertTriangle size={20} />
                   <p className="text-sm font-bold">{error}</p>
                </div>
                <button onClick={() => setError(null)} className="mt-2 text-[10px] font-black uppercase text-gray-400 hover:text-gray-600 tracking-widest">Dismiss</button>
             </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8 pb-20">
          {/* Practice Identity */}
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-black text-brand-900 mb-6 flex items-center gap-2 uppercase tracking-tight">
                <Building2 size={20} /> Practice Identity
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Practice / Firm Name</label>
                  <input required type="text" value={formData.businessName || ''} onChange={e => setFormData(prev => ({ ...prev, businessName: e.target.value }))} className="w-full rounded-xl bg-gray-50 border-gray-200 py-3 px-4 outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Primary Discipline</label>
                  <select value={formData.category} onChange={e => setFormData(prev => ({ ...prev, category: e.target.value as Category }))} className="w-full rounded-xl bg-gray-50 border-gray-200 py-3 px-4 outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-black uppercase text-xs tracking-widest">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">ABN (Australian Business Number)</label>
                  <input required type="text" value={formData.abn || ''} onChange={e => setFormData(prev => ({ ...prev, abn: e.target.value }))} className="w-full rounded-xl bg-gray-50 border-gray-200 py-3 px-4 outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium" placeholder="11 222 333 444" />
                </div>
            </div>
          </div>

          {/* Business Location */}
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-black text-brand-900 mb-6 flex items-center gap-2 uppercase tracking-tight">
                <MapPin size={20} /> Business Location
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Physical Address</label>
                  <input required type="text" value={formData.location?.address || ''} onChange={e => updateNestedLocation('address', e.target.value)} className="w-full rounded-xl bg-gray-50 border-gray-200 py-3 px-4 outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium" placeholder="e.g. Level 10, 459 Collins Street" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Suburb / City</label>
                  <input required type="text" value={formData.location?.suburb || ''} onChange={e => updateNestedLocation('suburb', e.target.value)} className="w-full rounded-xl bg-gray-50 border-gray-200 py-3 px-4 outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium" placeholder="e.g. Melbourne" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">State</label>
                  <select value={formData.location?.state} onChange={e => updateNestedLocation('state', e.target.value)} className="w-full rounded-xl bg-gray-50 border-gray-200 py-3 px-4 outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-black uppercase text-xs tracking-widest">
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
            </div>
          </div>

          {/* Branding & Assets */}
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-black text-brand-900 mb-6 flex items-center gap-2 uppercase tracking-tight">
                <Upload size={20} /> Branding & Visuals
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Business Logo</label>
                <div className="mt-2 flex items-center gap-4">
                  <div className="w-20 h-20 rounded-2xl bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden">
                    {formData.logoUrl ? <img src={formData.logoUrl} className="w-full h-full object-contain" /> : <ImageIcon size={24} className="text-gray-300" />}
                  </div>
                  <label className="cursor-pointer bg-brand-50 text-brand-600 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-brand-100 transition">
                    Upload Logo
                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'logoUrl')} />
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Cover Image / Office Photo</label>
                <div className="mt-2 flex items-center gap-4">
                  <div className="flex-1 h-20 rounded-2xl bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden">
                    {formData.imageUrl ? <img src={formData.imageUrl} className="w-full h-full object-cover" /> : <ImageIcon size={24} className="text-gray-300" />}
                  </div>
                  <label className="cursor-pointer bg-brand-50 text-brand-600 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-brand-100 transition">
                    Upload Cover
                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'imageUrl')} />
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-black text-brand-900 mb-6 flex items-center gap-2 uppercase tracking-tight">
                <Phone size={20} /> Contact Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Business Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    <input required type="tel" value={formData.contact?.phone || ''} onChange={e => updateNestedContact('phone', e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-50 border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium" placeholder="03 9000 0000" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Business Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    <input required type="email" value={formData.contact?.email || ''} onChange={e => updateNestedContact('email', e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-50 border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium" placeholder="office@practice.com.au" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Website URL</label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    <input type="text" value={formData.contact?.website || ''} onChange={e => updateNestedContact('website', e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-50 border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium" placeholder="e.g. www.yourpractice.com.au" />
                  </div>
                </div>
            </div>
          </div>

          {/* Project Gallery */}
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-black text-brand-900 flex items-center gap-2 uppercase tracking-tight">
                  <ImageIcon size={20} /> Project Showcase
              </h3>
              <button type="button" onClick={addGalleryItem} className="flex items-center gap-2 bg-brand-50 text-brand-600 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-brand-100 transition shadow-sm">
                <Plus size={14} /> Add Project Image
              </button>
            </div>
            
            <div className="space-y-6">
              {(formData.gallery || []).map((item, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 flex flex-col md:flex-row gap-6 relative group">
                  <button type="button" onClick={() => removeGalleryItem(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white p-1.5 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition">
                    <Trash2 size={14} />
                  </button>
                  <div className="w-full md:w-48 h-32 rounded-xl bg-white border border-gray-200 overflow-hidden flex items-center justify-center relative">
                    {item.url ? (
                      <img src={item.url} className="w-full h-full object-cover" />
                    ) : (
                      <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition">
                        <Upload size={20} className="text-gray-300 mb-1" />
                        <span className="text-[8px] font-black uppercase text-gray-400">Select Image</span>
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleGalleryFileUpload(e, idx)} />
                      </label>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="block text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Project Description</label>
                    <textarea value={item.description} onChange={e => updateGalleryItem(idx, 'description', e.target.value)} className="w-full rounded-xl border-gray-200 bg-white py-3 px-4 outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium text-sm" rows={3} placeholder="Describe this project and your role..." />
                  </div>
                </div>
              ))}
              {(formData.gallery || []).length === 0 && (
                <div className="text-center py-10 border-2 border-dashed border-gray-100 rounded-3xl">
                  <ImageIcon size={32} className="text-gray-200 mx-auto mb-2" />
                  <p className="text-xs font-bold text-gray-400 uppercase">No project photos added yet.</p>
                </div>
              )}
            </div>
          </div>

          {/* Biography */}
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-black text-brand-900 mb-6 flex items-center gap-2 uppercase tracking-tight">
                <FileText size={20} /> Practice Description
            </h3>
            <div className="space-y-6">
              <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">About Your Firm</label>
                  <textarea required value={descriptionBuffer} onChange={e => setDescriptionBuffer(e.target.value)} rows={10} className="w-full rounded-2xl bg-gray-50 border-gray-200 py-4 px-4 outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium leading-relaxed" placeholder="Share your firm's professional mission and key specialisations..." />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6">
            <button type="submit" disabled={saving} className="bg-brand-900 hover:bg-black text-white font-black px-12 py-4 rounded-2xl shadow-xl flex items-center gap-3 transition uppercase tracking-widest text-xs disabled:opacity-50 active:scale-95 transform">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {saving ? 'Syncing Records...' : 'Save & Publish'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ListingFormPage;
